import React from 'react';
import { Typography } from 'antd';
import LegalPage from 'common/components/LegalPage';

const { Title, Paragraph } = Typography;

// One policy for every app and website Acts 2 Network runs (Tribe, A2N Hymnal, our sites), so
// each store listing can link here. Keep it general: describe what our apps and sites do as a
// group, rather than what each one does.
const PrivacyPolicyPage = () => (
  <LegalPage effectiveDate="October 8, 2026" title="Privacy Policy">
    <Paragraph>
      This Privacy Policy explains how Acts 2 Network (&ldquo;Acts 2 Network,&rdquo;
      &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;) collects, uses, and protects
      information when you use the apps, websites, and online services we run (together, the
      &ldquo;Services&rdquo;). These include our membership management application, a private
      tool for managing membership records, and apps and websites open to the public. By using
      the Services you agree to the practices described below.
    </Paragraph>

    <Title level={3}>Information We Collect</Title>
    <Paragraph>
      We collect only the information needed to run the Services:
    </Paragraph>
    <Paragraph>
      <ul>
        <li>
          <strong>Account information.</strong> Where a Service requires you to sign in, we use
          Google&rsquo;s OAuth service to verify your identity. We receive your name and email
          address from your Google account. We do not receive or store your Google password.
          Some of our apps do not require an account.
        </li>
        <li>
          <strong>Member profile information.</strong> In our membership management
          application, authorized staff and volunteers may record member details such as name,
          contact information, birthday, photographs, family relationships, and ministry or
          group involvement.
        </li>
        <li>
          <strong>Usage information.</strong> Our apps and websites record how they are used:
          for example, which pages, screens, and features are opened, along with the type of
          device, operating system, app version, and approximate location (country or region)
          derived from your IP address. In apps that do not require an account, this
          information is associated only with a random identifier created on your device, not
          with your name or email address. In apps you sign in to, it may be associated with
          your account. Our membership management application also keeps change-history
          records of edits so that we can maintain accurate member data.
        </li>
      </ul>
    </Paragraph>

    <Title level={3}>Analytics and Cookies</Title>
    <Paragraph>
      We use analytics services to understand how the Services are used, so that we can fix
      problems and improve them. Our apps use Mixpanel, and our websites use Google Analytics,
      which uses cookies and similar technologies in your browser. We do not use analytics
      information for advertising, we do not sell it, and we do not combine it with
      information from other companies to track you across other apps and websites.
    </Paragraph>
    <Paragraph>
      You can block or delete cookies through your browser settings, or install the{' '}
      <a href="https://tools.google.com/dlpage/gaoptout" rel="noopener noreferrer" target="_blank">
        Google Analytics opt-out browser add-on
      </a>
      . Deleting an app removes the random identifier it created on your device.
    </Paragraph>

    <Title level={3}>How We Use Information</Title>
    <Paragraph>
      We use the information we collect solely to operate and improve the Services, including
      maintaining membership records and communicating with members. We do not use
      member information for advertising.
    </Paragraph>

    <Title level={3}>How We Share Information</Title>
    <Paragraph>
      We do not sell personal information, and we do not share it with third parties for their
      own marketing. We share information only with:
    </Paragraph>
    <Paragraph>
      <ul>
        <li>
          <strong>Service providers</strong> who help us run the Services, including Google
          (for sign-in and website analytics), Mixpanel (for app analytics), and Amazon Web
          Services (for hosting and file storage). These providers process information on our
          behalf and are bound to protect it.
        </li>
        <li>
          <strong>Authorized members</strong> of Acts 2 Network who have been granted access
          to our membership management application.
        </li>
        <li>
          <strong>Legal authorities</strong> when required by law.
        </li>
      </ul>
    </Paragraph>

    <Title level={3}>Data Retention</Title>
    <Paragraph>
      We retain member information for as long as it is needed to maintain membership records,
      and usage information for as long as it is useful for improving the
      Services. You may request that we update or remove your information by contacting us
      using the details below.
    </Paragraph>

    <Title level={3}>Security</Title>
    <Paragraph>
      Access to member information requires sign-in and is limited to authorized members. We
      use reasonable administrative and technical safeguards to protect the information we
      hold, though no method of transmission or storage is completely secure.
    </Paragraph>

    <Title level={3}>Children&rsquo;s Privacy</Title>
    <Paragraph>
      Information about minors in our membership management application is entered and
      managed only by their parents or guardians, and the application is not intended for use by children.
      We do not knowingly collect personal information directly from children
      under 13. Apps that children may use without an account collect only the anonymous
      usage information described above.
    </Paragraph>

    <Title level={3}>Your Choices and Rights</Title>
    <Paragraph>
      You may ask us to access, correct, or delete the personal information we hold about you.
      To make a request, please contact us using the information below.
    </Paragraph>

    <Title level={3}>Changes to This Policy</Title>
    <Paragraph>
      We may update this Privacy Policy from time to time. When we do, we will revise the
      effective date above and, where appropriate, provide additional notice.
    </Paragraph>

    <Title level={3}>Contact Us</Title>
    <Paragraph>
      If you have questions about this Privacy Policy or how your information is handled,
      please contact us at:
    </Paragraph>
    <Paragraph>
      Acts 2 Network
      <br />
      1275 Harbor Bay Parkway
      <br />
      Alameda, CA 94502
      <br />
      privacy@acts2.network
    </Paragraph>
  </LegalPage>
);

export default PrivacyPolicyPage;
